import { alpha } from "@mui/material/styles";
import { buttonClasses } from "@mui/material/Button";
// ----------------------------------------------------------------------
const COLORS = [
    "primary",
    "secondary",
    "info",
    "success",
    "warning",
    "error",
];
// ----------------------------------------------------------------------
export function button(theme) {
    const lightMode = theme.palette.mode === "light";
    const rootStyles = (ownerState) => {
        const inheritColor = ownerState.color === "inherit";
        const xlSize = ownerState.size === "xl";
        const containedVariant = ownerState.variant === "contained";
        const outlinedVariant = ownerState.variant === "outlined";
        const textVariant = ownerState.variant === "text";
        const softVariant = ownerState.variant === "soft";
        const smallSize = ownerState.size === "small";
        const mediumSize = ownerState.size === "medium";
        const largeSize = ownerState.size === "large";
        const defaultStyle = {
            ...(inheritColor && {
                // CONTAINED
                ...(containedVariant && {
                    color: lightMode
                        ? theme.palette.common.white
                        : theme.palette.grey[800],
                    backgroundColor: lightMode
                        ? theme.palette.grey[800]
                        : theme.palette.common.white,
                    "&:hover": {
                        backgroundColor: lightMode
                            ? theme.palette.grey[700]
                            : theme.palette.grey[400],
                    },
                }),
                // OUTLINED
                ...(outlinedVariant && {
                    borderColor: alpha(theme.palette.grey[500], 0.32),
                    "&:hover": {
                        backgroundColor: theme.palette.action.hover,
                    },
                }),
                // TEXT
                ...(textVariant && {
                    "&:hover": {
                        backgroundColor: theme.palette.action.hover,
                    },
                }),
                // SOFT
                ...(softVariant && {
                    color: theme.palette.text.primary,
                    backgroundColor: alpha(theme.palette.grey[500], 0.08),
                    "&:hover": {
                        backgroundColor: alpha(theme.palette.grey[500], 0.24),
                    },
                }),
            }),
            ...(outlinedVariant && {
                "&:hover": {
                    borderColor: "currentColor",
                    boxShadow: "0 0 0 0.5px currentColor",
                },
            }),
        };
        const colorStyle = COLORS.map((color) => ({
            ...(ownerState.color === color && {
                // CONTAINED
                ...(containedVariant && {
                    "&:hover": {
                        boxShadow: theme.customShadows[color],
                    },
                }),
                // SOFT
                ...(softVariant && {
                    color: theme.palette[color][lightMode ? "dark" : "light"],
                    backgroundColor: alpha(theme.palette[color].main, 0.16),
                    "&:hover": {
                        backgroundColor: alpha(theme.palette[color].main, 0.32),
                    },
                }),
            }),
        }));
        // Add the custom ePrimary color style
        const ePrimaryStyle = {
            ...(ownerState.color === "ePrimary" && {
                // CONTAINED ePrimary
                ...(containedVariant && {
                    color: theme.palette.common.white,
                    backgroundColor: theme.palette.edark.main,
                    borderRadius: "16px",
                    fontSize: "16px !important",
                    fontWeight: "500 !important",
                    "&:hover": {
                        backgroundColor: "#003572",
                        boxShadow: theme.customShadows.z1,
                    },
                }),
                // SOFT ePrimary
                ...(softVariant && {
                    color: theme.palette.primary.main,
                    fontSize: "16px !important",
                    fontWeight: "500 !important",
                    backgroundColor: alpha(theme.palette.primary.main, 0.16),
                    "&:hover": {
                        backgroundColor: alpha(theme.palette.primary.main, 0.32),
                    },
                }),
                ...(textVariant && {
                    "&:hover": {
                        backgroundColor: theme.palette.action.hover,
                        fontWeight: "500 !important",
                        fontSize: "14px !important",
                    },
                }),
                // OUTLINED ePrimary
                ...(outlinedVariant && {
                    borderColor: theme.palette.edark.main,
                    color: theme.palette.edark.main,
                    fontSize: "16px !important",
                    fontWeight: "500 !important",
                    borderRadius: "16px",
                    "&:hover": {
                        backgroundColor: "transparent",
                        borderColor: alpha(theme.palette.edark.main, 0.08),
                    },
                }),
            }),
        };
        const eSuccessStyle = {
            ...(ownerState.color === "eSuccess" && {
                // CONTAINED eSuccess
                ...(containedVariant && {
                    color: theme.palette.common.white,
                    backgroundColor: "#0D9B31",
                    fontWeight: "500 !important",
                    borderRadius: "16px",
                    "&:hover": {
                        backgroundColor: alpha("#0D9B31", 0.9),
                        boxShadow: theme.customShadows.z1,
                    },
                }),
                // SOFT eSuccess
                ...(softVariant && {
                    color: theme.palette.success.main,
                    fontWeight: "500 !important",
                    backgroundColor: alpha(theme.palette.success.main, 0.16),
                    "&:hover": {
                        backgroundColor: alpha(theme.palette.success.main, 0.32),
                    },
                }),
                // OUTLINED eSuccess
                ...(outlinedVariant && {
                    borderColor: theme.palette.success.main,
                    fontWeight: "500 !important",
                    color: theme.palette.success.main,
                    borderRadius: "16px",
                    "&:hover": {
                        backgroundColor: "transparent",
                        borderColor: alpha(theme.palette.success.main, 0.08),
                    },
                }),
            }),
        };
        const disabledState = {
            [`&.${buttonClasses.disabled}`]: {
                // SOFT
                ...(softVariant && {
                    backgroundColor: theme.palette.action.disabledBackground,
                }),
            },
        };
        const size = {
            ...(smallSize && {
                height: 30,
                fontSize: 13,
                paddingLeft: 8,
                paddingRight: 8,
                fontWeight: 700,
                borderRadius: 10,
                ...(textVariant && {
                    paddingLeft: 4,
                    paddingRight: 4,
                }),
            }),
            ...(mediumSize && {
                padding: "8px 24px",
                fontSize: 14,
                fontWeight: 700,
                lineHeight: "26px",
                borderRadius: 14,
                ...(textVariant && {
                    paddingLeft: 8,
                    paddingRight: 8,
                }),
            }),
            ...(largeSize && {
                height: 48,
                fontSize: 15,
                paddingLeft: 16,
                paddingRight: 16,
                fontWeight: 700,
                borderRadius: 16,
                ...(textVariant && {
                    paddingLeft: 10,
                    paddingRight: 10,
                }),
            }),
            ...(xlSize && {
                height: 56,
                fontSize: 15,
                paddingTop: 12,
                paddingBottom: 12,
                paddingLeft: 24,
                paddingRight: 24,
                fontWeight: 700,
                borderRadius: 16,
                gap: 12,
                ...(textVariant && {
                    paddingLeft: 12,
                    paddingRight: 12,
                }),
            }),
        };
        return [
            defaultStyle,
            ...colorStyle,
            ePrimaryStyle,
            eSuccessStyle,
            disabledState,
            size,
        ];
    };
    return {
        MuiButton: {
            styleOverrides: {
                root: ({ ownerState }) => rootStyles(ownerState),
            },
        },
    };
}
